import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    CircularProgress,
    IconButton,
    TextField,
    Tooltip,
    useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import type { CaptchaResponse } from "@/types/CaptchaTypes";
import { getCaptcha } from "@/api/captchaApi";

export interface CaptchaSolution {
    answer: string;
    payload: string;
}

interface ParizCaptchaProps {
    onSolved: (solution: CaptchaSolution) => void;
    onChange?: (solution: CaptchaSolution | null) => void;
    onFailed?: (message: string) => void;
    error?: string;
}

const CAPTCHA_LENGTH = 6;

const ParizCaptcha: React.FC<ParizCaptchaProps> = ({
    onSolved,
    onChange,
    onFailed,
    error,
}) => {
    const theme = useTheme();

    const [data, setData] = useState<CaptchaResponse | null>(null);
    const [answer, setAnswer] = useState("");
    const [loading, setLoading] = useState(false);
    const [localError, setLocalError] = useState("");

    const lastSubmittedRef = useRef<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLocalError("");
        setAnswer("");
        setData(null);
        lastSubmittedRef.current = null;

        onChange?.(null);

        try {
            const captcha = await getCaptcha();
            setData(captcha);
        } catch {
            const message = "خطا در دریافت کپچا";
            setLocalError(message);
            onFailed?.(message);
        } finally {
            setLoading(false);
        }
    }, [onChange, onFailed]);

    useEffect(() => {
        void load();

        // Load the CAPTCHA when the component mounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!error) return;

        setLocalError(error);
        void load();
    }, [error, load]);

    const submit = useCallback(
        (value: string) => {
            const trimmed = value.trim();

            if (trimmed.length !== CAPTCHA_LENGTH || !data) {
                return;
            }

            const key = `${data.payload}:${trimmed}`;

            if (lastSubmittedRef.current === key) {
                return;
            }

            lastSubmittedRef.current = key;
            setLocalError("");

            onSolved({
                answer: trimmed,
                payload: data.payload,
            });
        },
        [data, onSolved],
    );

    useEffect(() => {
        if (loading || !data) return;
        if (answer.trim().length !== CAPTCHA_LENGTH) return;

        submit(answer);
    }, [answer, data, loading, submit]);

    useEffect(() => {
        if (!data || !answer.trim()) {
            onChange?.(null);
            return;
        }

        const trimmed = answer.trim();

        onChange?.({
            answer: trimmed,
            payload: data.payload,
        });
    }, [answer, data, onChange]);

    const handleChange = (value: string) => {
        // Prevent duplicate submissions when the answer changes.
        lastSubmittedRef.current = null;

        setAnswer(value);
    };

    const disabled = loading;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box",
            }}
        >
            {/* CAPTCHA image + input + refresh button */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: {
                        xs: "column",
                        sm: "row",
                    },
                    alignItems: {
                        xs: "stretch",
                        sm: "center",
                    },
                    gap: 1,
                    width: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                }}
            >
                {/* Responsive CAPTCHA image */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: {
                            xs: "center",
                            sm: "flex-start",
                        },
                        flex: {
                            xs: "0 1 auto",
                            sm: "0 0 auto",
                        },
                        width: {
                            xs: "100%",
                            sm: "auto",
                        },
                        maxWidth: "100%",
                        minWidth: 0,
                        minHeight: 48,
                        height: 48,
                        overflow: "hidden",
                        borderRadius: 1,
                        boxSizing: "border-box",
                    }}
                >
                    {loading ? (
                        <CircularProgress size={20} />
                    ) : data?.base64 ? (
                        <Box
                            component="img"
                            src={data.base64}
                            alt="CAPTCHA verification code"
                            draggable={false}
                            sx={{
                                display: "block",
                                width: "auto",
                                height: "auto",
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                flexShrink: 1,
                                userSelect: "none",
                            }}
                        />
                    ) : null}
                </Box>

                {/* Input + refresh button */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flex: "1 1 0%",
                        width: {
                            xs: "100%",
                            sm: "auto",
                        },
                        minWidth: 0,
                        boxSizing: "border-box",
                    }}
                >
                    <TextField
                        size="small"
                        value={answer}
                        onChange={(event) =>
                            handleChange(event.target.value)
                        }
                        placeholder="کد تصویر"
                        inputProps={{
                            maxLength: CAPTCHA_LENGTH,
                            dir: "ltr",
                            "aria-label": "کد تصویر",
                            autoComplete: "off",
                            style: {
                                textAlign: "center",
                                letterSpacing: 2,
                            },
                        }}
                        sx={{
                            flex: "1 1 0%",
                            minWidth: 0,
                            "& .MuiInputBase-root": {
                                minWidth: 0,
                            },
                        }}
                        disabled={disabled}
                        fullWidth
                    />

                    <Tooltip title="تصویر جدید">
                        <span>
                            <IconButton
                                onClick={() => void load()}
                                disabled={disabled}
                                aria-label="بارگذاری تصویر جدید"
                                sx={{
                                    flexShrink: 0,
                                }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* Error message */}
            {localError && (
                <Box
                    role="alert"
                    sx={{
                        color: theme.palette.error.main,
                        fontSize: 12,
                        px: 0.5,
                        overflowWrap: "anywhere",
                    }}
                >
                    {localError}
                </Box>
            )}
        </Box>
    );
};

export default ParizCaptcha;